import { useState, useEffect } from "react";
import { apiClient } from "../../../../globals/utils/api";
import { Box, Typography, CircularProgress, TextField, Button } from "@mui/material";
import Select from 'react-select';
export const BondNumberSelect = ({ data, updateData }) => {
    const [bondNumberOptions, setBondNumberOptions] = useState([]);
    const [selectedBondNumber, setSelectedBondNumber] = useState(null);
    const [loading, setLoading] = useState(false);
    const [newBondNumber, setNewBondNumber] = useState(''); // Input for creating new bond number
    const [creating, setCreating] = useState(false); // Loading state for bond creation
    const [hasSetDefault, setHasSetDefault] = useState(false); // Used to track if the default selection has been applied
  
    // **Fetch the list of bond numbers from the API - runs only on component mount**
    useEffect(() => {
      const fetchBondNumbers = async () => {
        setLoading(true);
        try {
          const response = await apiClient('/bondnumbers/', 'GET');
          const formattedOptions = response.map((bond) => ({
            label: `${bond.bond_number}`,
            value: bond.bond_number,
            ...bond, // Store the entire bond object so we can access it on selection
          }));
          
          setBondNumberOptions(formattedOptions.reverse());
        } catch (error) {
          console.error('Error fetching bond numbers:', error);
        } finally {
          setLoading(false);
        }
      };
  
      fetchBondNumbers();
    }, []); // Empty dependency array, only runs on mount
  
    // **Set pre-filled bond number (only runs after bondNumberOptions are loaded)**
    useEffect(() => {
      if (!hasSetDefault && bondNumberOptions.length > 0) {
        if (data?.bond_number?.bond_number) {
          const preFilledBondNumber = bondNumberOptions.find(
            (option) => option.value === data.bond_number.bond_number
          );
          
          if (preFilledBondNumber) {
            setSelectedBondNumber(preFilledBondNumber);
            updateData({ ...data, bond_number: preFilledBondNumber }); // Update parent component with the pre-filled bond number
          }
        } else if (bondNumberOptions.length > 0) {
          // Auto-select the first available bond number that is not marked as is_used
          const availableBondNumber = bondNumberOptions.find(option => !option.is_used);
          if (availableBondNumber) {
            setSelectedBondNumber(availableBondNumber);
            updateData({ ...data, bond_number: availableBondNumber }); // Update parent component with the first bond number
          }
        }
        setHasSetDefault(true); // Prevent re-execution
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bondNumberOptions, data]);
  
    // Handle selection change from the dropdown
    const handleChange = (selectedOption) => {
      setSelectedBondNumber(selectedOption);
      updateData({ ...data, bond_number: selectedOption }); // Update parent component with selected option
    };
  
    // Handle the creation of a new bond number
    const handleCreateBondNumber = async () => {
      if (!newBondNumber) {
        alert('Please enter a bond number.');
        return;
      }
  
      try {
        setCreating(true);
        const response = await apiClient('/single-bondnumber/', 'POST', { bond_number: newBondNumber });
  
        // Format the response to match the format used in react-select
        const newOption = {
          label: `${response.bond_number}`,
          value: response.bond_number,
          ...response,
        };
  
        // Add the newly created bond number to the dropdown options
        setBondNumberOptions((prevOptions) => [...prevOptions, newOption]);
  
        // Automatically select the new bond number
        setSelectedBondNumber(newOption);
        updateData({ ...data, bond_number: newOption }); // Update parent component with the new bond number
  
        // Reset the input field for bond number
        setNewBondNumber('');
      } catch (error) {
        console.error('Error creating bond number:', error);
        alert(`Failed to create bond number: ${error.response?.data?.error || 'Unknown error'}`);
      } finally {
        setCreating(false);
      }
    };
  
    return (
      <Box
        className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
        sx={{ border: '1px solid #e0e0e0' }}
      >
        <Typography variant="h6" className="mb-2">
          Select a Bond Number
        </Typography>
  
        {loading ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Select
            options={bondNumberOptions}
            value={selectedBondNumber}
            onChange={handleChange}
            isClearable
            isSearchable
            placeholder="Select or create a bond number"
            getOptionLabel={(option) => {
              if (option === selectedBondNumber) {
                return `${option.label} ✅`; // Show checkmark for selected option
              }
              return `${option.label} ${option.is_used ? '❌' : '🟢'}`; // Show red cross or green circle
            }}
            renderOption={(props, option) => (
              <li {...props}>
                {option.label} {option.is_used ? '❌' : '🟢'}
              </li>
            )}
            getOptionValue={(e) => e.value} // Unique key for options
            styles={{
                control: (base, state) => ({
                ...base,
                height: 56,
                borderRadius: 4,
                borderColor: state.isFocused ? '#6200ea' : '#e0e0e0',
                boxShadow: state.isFocused ? '0 0 0 2px rgba(98, 0, 234, 0.2)' : 'none',
                '&:hover': {
                    borderColor: '#6200ea',
                },
                backgroundColor: '#ffffff',
                opacity: 1, // Ensure it's fully visible
                }),
                menu: (base) => ({
                ...base,
                zIndex: 9999, // Increase z-index
                }),
                menuPortal: (base) => ({
                ...base,
                zIndex: 9999, // If using menuPortal
                }),
                option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#6200ea' : state.isFocused ? '#f0f0f0' : '#fff',
                color: state.isSelected ? '#fff' : '#000',
                '&:hover': {
                    backgroundColor: '#f0f0f0',
                },
                }),
            }}
            />
  
            <Typography variant="h6" className="mt-6 font-normal">
              Or create a new bond number
            </Typography>
  
            <Box display="flex" gap={2} mt={2}>
              <TextField
                label="Enter Bond Number"
                value={newBondNumber}
                onChange={(e) => setNewBondNumber(e.target.value)}
                fullWidth
              />
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleCreateBondNumber} 
                disabled={creating}
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  };